import React, { ReactNode, useState } from "react";
import { FilePickerErrorType, useFilePickerState } from "../../states";
import {
  FilePickerError,
  FilePickerImagePreview,
  GuideModal,
} from "../../components";
import Loader from "@asphalt-react/loader";
import { Box } from "../../../Box";
import { ReactSVG } from "react-svg";
import {
  MediaDeviceType,
  useMediaDevice,
  useTranslation,
} from "../../../../hooks";
import { CaptureModal } from "./Capture.modal";
import { Button } from "../../../Button";
import styled from "styled-components";
import { Text } from "../../../Text";
import { dataURLtoFile, generateUUID } from "../../../../helpers";
import { observer } from "mobx-react-lite";
import { ErrorActions } from "./ErrorActions";

const HelperTextEl = styled(Text)`
  color: rgba(31, 36, 42, 0.5);
  line-height: 14px;
`;

export type IFilePickerContentProps = {
  getErrorTranslation?: (error: FilePickerErrorType) => string;
  guideProps?: {
    actionText: string;
    content: ReactNode;
  };
  contentProps: {
    helperText?: string;
    takePicText: string;
    uploadText: string;
    reUploadText: string;
    reTakePicText: string;
  };
};

export const FilePickerContent: React.FC<IFilePickerContentProps> = observer(
  (props) => {
    const translate = useTranslation();
    const { hasDevice: hasCamera, requestPermission } = useMediaDevice(
      MediaDeviceType.VIDEO_INPUT
    );
    const [showGuide, setShowGuide] = useState<boolean>(false);
    const [showCamera, setShowCamera] = useState<boolean>(false);
    const [source, setSource] = useState<"camera" | "file" | undefined>(
      undefined
    );
    const { getErrorTranslation, guideProps, contentProps } = props;
    const { helperText, uploadText, takePicText, reTakePicText, reUploadText } =
      contentProps || {};

    const {
      error,
      isLoading,
      previewUrl,
      inputRef: uploadRef,
      onDrop,
      maxSize,
      deleteFile,
    } = useFilePickerState();

    const handleCameraClick = () => {
      setSource("camera");
      guideProps ? setShowGuide(true) : setShowCamera(true);
    };

    const handleFileUpload = () => {
      setSource("file");
      uploadRef?.current?.click();
    };

    if (error) {
      return (
        <FilePickerError
          getTranslation={getErrorTranslation}
          actions={
            <ErrorActions
              hasCamera={hasCamera!!}
              onRetryUpload={() => {
                deleteFile();
                handleFileUpload();
              }}
              onRetryCamera={() => {
                deleteFile();
                handleCameraClick();
              }}
              source={source}
              translation={{
                retakePic: reTakePicText,
                takePic: takePicText,
                reUpload: reUploadText,
                upload: uploadText,
              }}
            />
          }
        />
      );
    }
    if (previewUrl && !isLoading) return <FilePickerImagePreview />;

    return (
      <>
        {isLoading ? (
          <Box marginBottomXl>
            <Loader size="l" />
          </Box>
        ) : (
          <Box style={{ position: "relative", width: "64px", height: "64px" }}>
            <ReactSVG
              src={require("../assets/icon_placeholder.svg")}
              style={{ position: "absolute" }}
            />
            <Box flex justifyCenter alignCenter style={{ height: "100%" }}>
              <ReactSVG src={require("../assets/icon_user.svg")} />
            </Box>
          </Box>
        )}

        {guideProps && (
          <GuideModal
            show={showGuide}
            onClose={() => setShowGuide(false)}
            onAction={async () => {
              const isAccepted = await requestPermission();
              setShowGuide(false);
              setShowCamera(isAccepted);
            }}
            actionText={guideProps.actionText}
            content={guideProps.content}
          />
        )}

        {showCamera && (
          <CaptureModal
            show={showCamera}
            onClose={() => setShowCamera(false)}
            onCaptured={(image: string) => {
              const uniqueId = generateUUID();
              onDrop([dataURLtoFile(image, `selfie-${uniqueId}.png`)]);
              setShowCamera(false);
            }}
          />
        )}

        <Box
          marginBottomXl
          marginTopXxl
          paddingRightXl
          paddingLeftXl
          width100
          flexDirectionRow
          justifyBetween
          flex
          style={{ gap: "1rem" }}
        >
          {hasCamera && (
            <Button
              width100
              flexGrow
              id="take-picture-btn"
              onClick={handleCameraClick}
            >
              {isLoading ? translate("uploading") : takePicText}
            </Button>
          )}
          <Button
            width100
            flexGrow
            outline
            id="upload-picture-btn"
            onClick={handleFileUpload}
          >
            {isLoading ? translate("uploading") : uploadText}
          </Button>
        </Box>
        <HelperTextEl
          id="selfie-helper-text"
          fontWeight="demi"
          fontSize="xsmall"
        >
          {helperText || translate("default_helper_text", [`${maxSize}MB`])}
        </HelperTextEl>
      </>
    );
  }
);
